import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Checkbox,
  InputHint,
  InputStatusMessage,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { HealthMetricInput } from '@leagueplatform/dashboard-api';

export type CheckboxFieldConfig = {
  metricIndex: number;
  valueField: HealthMetricInput['valueFields'][number];
  defaultValue?: number;
};

type CheckboxListProps = {
  checkboxFields: CheckboxFieldConfig[];
  validationMessage?: string;
};

export function CheckboxList({
  checkboxFields,
  validationMessage,
}: CheckboxListProps) {
  const {
    formState: { errors },
    getValues,
    trigger,
  } = useFormContext();

  // Create a unique group field name based on the checkbox fields
  const groupFieldName = React.useMemo(
    () =>
      `checkbox-group-${checkboxFields
        .map(
          ({ metricIndex, valueField }) =>
            `${metricIndex}-${valueField.fieldKeyName}`,
        )
        .join('-')}`,
    [checkboxFields],
  );

  const groupError = errors?.[groupFieldName]?.message as string | undefined;

  return (
    <Box>
      <Controller
        name={groupFieldName}
        defaultValue={null}
        rules={{
          validate: () => {
            // Check if at least one checkbox is selected
            const isAnySelected = checkboxFields.some(
              ({ metricIndex, valueField, defaultValue = 0 }) => {
                const fieldName = `${metricIndex}-${valueField.fieldKeyName}`;
                // Check current form value, falling back to defaultValue if undefined
                const value = getValues(fieldName) ?? defaultValue;
                return value === 1 || value === '1';
              },
            );

            return isAnySelected || validationMessage;
          },
        }}
        render={() => <input type="hidden" />}
      />
      <StackLayout spacing="$one" horizontalAlignment="stretch">
        {checkboxFields.map(({ metricIndex, valueField, defaultValue = 0 }) => {
          const fieldName = `${metricIndex}-${valueField.fieldKeyName}`;

          return (
            <Controller
              key={fieldName}
              name={fieldName}
              defaultValue={defaultValue}
              render={({ field }) => (
                <StackLayout spacing="$half">
                  <Checkbox
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...field}
                    id={fieldName}
                    name={fieldName}
                    checked={field.value === 1}
                    value={1}
                    label={valueField.fieldLabel}
                    onChange={(event) => {
                      field.onChange(event.target.checked ? 1 : 0);
                      trigger(groupFieldName);
                    }}
                  />
                  {valueField.hint ? (
                    <InputHint>{valueField.hint}</InputHint>
                  ) : null}
                </StackLayout>
              )}
            />
          );
        })}
        {groupError ? (
          <InputStatusMessage inputStatus="error">
            {groupError}
          </InputStatusMessage>
        ) : null}
      </StackLayout>
    </Box>
  );
}
